// Cliente del flujo de signup self-service (/registro).
// Llama a auth-tenant-svc → POST /v1/public/signup vía gateway.

export type PlanSlug = 'clinica-solo' | 'clinica' | 'clinica-plus';

export type PlanInfo = {
  slug: PlanSlug;
  nombre: string;
  precio: number;        // Q/mes
  desde?: boolean;       // "desde Qxxx"
  resumen: string;
  modulos: string[];
  limiteUsuarios: number | 'ilimitado';
  destacado?: boolean;
};

export const PLANES: PlanInfo[] = [
  {
    slug: 'clinica-solo',
    nombre: 'Clínica · Solo',
    precio: 99,
    resumen: 'Consultorio privado: agenda, expediente CUI, cobros FEL y farmacia de mostrador.',
    modulos: ['Agenda y pacientes', 'Expediente clínico', 'Facturación FEL', 'Farmacia / punto de venta', 'Apoyo LIS/RIS (medicina general)', 'Hasta 1 usuario'],
    limiteUsuarios: 1,
  },
  {
    slug: 'clinica',
    nombre: 'Clínica',
    precio: 299,
    resumen: 'Clínica privada con varios profesionales, caja y aseguradoras.',
    modulos: ['Todo lo de Solo', 'Hasta 3 usuarios', 'Convenios con aseguradoras', 'Reportes de caja'],
    limiteUsuarios: 3,
    destacado: true,
  },
  {
    slug: 'clinica-plus',
    nombre: 'Clínica · Plus',
    precio: 499,
    resumen: 'Multi-sede o clínica con inventario y tesorería. Sin vínculo con hospitales.',
    modulos: ['Todo lo de Clínica', 'Multi-sede', 'Bodega e inventario', 'Tesorería', 'Usuarios ilimitados'],
    limiteUsuarios: 'ilimitado',
  },
];

export type SignupPayload = {
  nombre: string;
  email: string;
  telefono: string;
  plan: PlanSlug;
  tipoOrganizacion:
    | 'clinica-medica' | 'clinica-odontologica' | 'clinica-veterinaria'
    | 'clinica-nutricion' | 'clinica-psicologia' | 'clinica-estetica'
    | 'clinica-optica' | 'clinica-fisioterapia';
  slugSugerido?: string;
  cargo?: string;
  whatsapp?: string;
  /** Usuario elegido por el cliente (sin @toscanasystems.com). */
  usuario: string;
  /** Contraseña elegida por el cliente. */
  password: string;
};

export type SignupResponse = {
  tenant: {
    tenantId: string;
    nombre: string;
    edicion: string;
    plan: string;
    estado: string;
    trialEndsAt: string;
  };
  /** Username generado por Toscana: `{slug}@toscanasystems.com`. */
  usuarioAdmin: string;
  /** Password temporal: sólo se devuelve UNA vez. Mostrar y permitir copiar. */
  tempPassword?: string;
  /** URL única de login para todos los tenants. */
  loginUrl: string;
  trialEndsAt: string;
  plan: {
    slug: string;
    nombre: string;
    precioMensualGtq: number;
    limiteUsuarios: number;
  };
  tipoOrganizacion?: string;
  /** Legacy: sólo presente mientras `hospital.security.mode=legacy`. */
  bootstrapToken?: string;
  /** Legacy: sólo presente con `bootstrapToken`. */
  expiresIn?: number;
};

export type StartSignupResponse = {
  email: string;
  mensaje: string;
};

export async function signup(payload: SignupPayload): Promise<StartSignupResponse> {
  const r = await fetch('/api/auth/v1/public/signup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    let code = 'error-desconocido';
    try {
      const body = await r.json() as { error?: string };
      code = body.error ?? code;
    } catch { /* ignore */ }
    throw new SignupError(code, r.status);
  }
  return r.json();
}

export async function verifyEmail(email: string, code: string): Promise<SignupResponse> {
  const r = await fetch('/api/auth/v1/public/verify-email', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  if (!r.ok) {
    let errCode = 'error-desconocido';
    try {
      const body = await r.json() as { error?: string };
      errCode = body.error ?? errCode;
    } catch { /* ignore */ }
    throw new SignupError(errCode, r.status);
  }
  return r.json();
}

export class SignupError extends Error {
  constructor(public code: string, public status: number) {
    super(code);
    this.name = 'SignupError';
  }
}

/** Traducción de códigos de error del backend a mensajes legibles. */
export function mensajeError(code: string): string {
  switch (code) {
    case 'nombre-requerido':       return 'El nombre de la clínica es obligatorio.';
    case 'email-invalido':         return 'El correo no tiene un formato válido.';
    case 'telefono-requerido':     return 'El teléfono es obligatorio.';
    case 'plan-invalido':          return 'Selecciona un plan válido.';
    case 'tipo-organizacion-invalido':
                                   return 'Selecciona el tipo de clínica.';
    case 'nombre-no-genera-slug-valido':
                                   return 'El nombre no permite generar un identificador. Usa letras y números.';
    case 'slug-saturado':          return 'Demasiadas clínicas con ese nombre. Intenta con un nombre más específico.';
    case 'keycloak-provisioning-failed':
                                   return 'Tu clínica se creó pero hubo un error al generar el usuario. Contacta soporte al WhatsApp 3786-3061.';
    case 'body-requerido':         return 'No se recibieron datos. Vuelve a intentar.';
    case 'email-y-codigo-requeridos':
                                   return 'Ingresa tu email y el código de verificación.';
    case 'código inválido o expirado':
                                   return 'El código no es válido. Revisalo y volvé a intentar.';
    case 'código expirado':
                                   return 'El código venció. Volvé a registrarte para recibir uno nuevo.';
    case 'email ya verificado':
                                   return 'Este email ya fue verificado. Iniciá sesión.';
    default:
      if (code.startsWith('tenantId inválido')) return 'El identificador sugerido no es válido (solo letras, números y guiones).';
      return `No se pudo crear la cuenta (${code}).`;
  }
}
